import { create } from 'zustand'
import { isAxiosError } from 'axios'
import { apiClient } from '@/lib/api'
import { useCallStore } from '@/stores/callStore'

interface User {
  id: string
  email: string
  name: string
  /**
   * Structured name parts kept alongside the composed `name`. Consumers must
   * use these rather than splitting `name` on a space — that corrupts any
   * record whose first name contains one (see the Profile form regression).
   */
  firstName?: string
  lastName?: string
  role: 'owner' | 'admin' | 'sales' | 'support' | 'viewer' | 'member'
  avatar?: string
  mustResetPassword?: boolean
  shareCalendarTitles?: boolean
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  loading: boolean
  mustResetPassword: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setUser: (user: User) => void
  patchUser: (patch: Partial<User>) => void
  clearMustReset: () => void
  restoreSession: () => Promise<void>
}

function mapUser(raw: Record<string, unknown>): User {
  return {
    id: raw.id as string,
    email: raw.email as string,
    name: (raw.name as string) ?? (`${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim() || (raw.email as string)),
    // Preserve the structured parts. Callers that need them (the Profile form)
    // must not re-derive them by splitting `name` — that silently corrupts any
    // record whose first name contains a space.
    firstName: typeof raw.firstName === 'string' ? raw.firstName : undefined,
    lastName: typeof raw.lastName === 'string' ? raw.lastName : undefined,
    role: ((raw.role as string)?.toLowerCase() ?? 'member') as User['role'],
    avatar: raw.image as string | undefined,
    mustResetPassword: raw.mustResetPassword as boolean | undefined,
    shareCalendarTitles:
      typeof raw.shareCalendarTitles === 'boolean' ? raw.shareCalendarTitles : undefined,
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  token: null,
  user: null,
  loading: true,
  mustResetPassword: false,

  login: async (email: string, password: string) => {
    try {
      const { data } = await apiClient.post('/api/auth/sign-in/email', { email, password })
      const user = mapUser(data.user)

      // Better Auth's session payload doesn't include app-specific fields
      // (mustResetPassword, shareCalendarTitles, …). /api/auth/me is the
      // authoritative source — merge those fields so downstream readers
      // (e.g. the calendar-privacy toggle in Settings → Availability) reflect
      // ground truth instead of defaults.
      let mustReset = user.mustResetPassword ?? false
      try {
        const meRes = await apiClient.get('/api/auth/me')
        const dbUser = meRes.data?.data?.user as Record<string, unknown> | undefined
        if (dbUser) {
          if (dbUser.mustResetPassword) mustReset = true
          if (typeof dbUser.shareCalendarTitles === 'boolean') {
            user.shareCalendarTitles = dbUser.shareCalendarTitles
          }
        }
      } catch { /* ignore */ }

      set({
        isAuthenticated: true,
        token: null,
        user,
        mustResetPassword: mustReset,
      })
    } catch (err) {
      set({ isAuthenticated: false, token: null, user: null, mustResetPassword: false })
      throw err
    }
  },

  logout: async () => {
    // Theme 6: tear down Twilio Device on logout so the next login (or an
    // attacker sharing the browser) doesn't inherit a registered Device
    // bound to the previous user's identity. destroyDevice is a no-op
    // when no device is active.
    try {
      useCallStore.getState().destroyDevice()
    } catch { /* never let a callStore bug block sign-out */ }

    try {
      // 3-second timeout prevents UI hang on dead/slow networks
      await Promise.race([
        apiClient.post('/api/auth/sign-out'),
        new Promise<void>(resolve => setTimeout(resolve, 3000)),
      ])
    } catch { /* ignore network errors — always clear local state */ }
    set({ isAuthenticated: false, token: null, user: null, mustResetPassword: false })
  },

  setUser: (user: User) => set({ user }),

  patchUser: (patch: Partial<User>) =>
    set((state) => (state.user ? { user: { ...state.user, ...patch } } : state)),

  clearMustReset: () => set({ mustResetPassword: false }),

  restoreSession: async () => {
    set({ loading: true })
    try {
      const { data } = await apiClient.get('/api/auth/get-session')
      if (data?.user) {
        const user = mapUser(data.user)

        // Same rationale as `login`: pull app-specific fields from /api/auth/me
        // so the in-memory user has shareCalendarTitles / mustResetPassword
        // populated. Without this the privacy toggle in Settings would always
        // start as `false` on page reload regardless of DB state.
        let mustReset = user.mustResetPassword ?? false
        try {
          const meRes = await apiClient.get('/api/auth/me')
          const dbUser = meRes.data?.data?.user as Record<string, unknown> | undefined
          if (dbUser) {
            if (dbUser.mustResetPassword) mustReset = true
            if (typeof dbUser.shareCalendarTitles === 'boolean') {
              user.shareCalendarTitles = dbUser.shareCalendarTitles
            }
          }
        } catch { /* ignore */ }

        set({
          isAuthenticated: true,
          token: null,
          user,
          mustResetPassword: mustReset,
          loading: false,
        })
        return
      }
    } catch (err) {
      if (isAxiosError(err) && !err.response) {
        // Temporary network failure — don't clear auth state or redirect to login
        set({ loading: false })
        throw err
      }
      // HTTP error (401 etc.) — no valid session; fall through to set loading: false
    }
    set({ loading: false })
  },
}))
